import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { TabbedListSection } from '@app/components/TabbedListSection';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

// One lookup a customer ran (HLRCustomerLookup). This page listed the
// provider cache instead, and tagged every row "unreachable": Telnyx doesn't
// report reachability, so that field was always empty.
interface LookupRow {
  id: string;
  created_at: string;
  customer_email: string;
  company_name: string;
  batch: string | null;
  phone_number: string;
  is_valid: boolean;
  is_ported: boolean;
  number_type: string;
  carrier_name: string;
  mccmnc: string;
  country_code: string;
  error: string;
  price: string;
  refunded: boolean;
  state: string;
}

interface BatchRow {
  id: string;
  user_email: string;
  filename: string;
  status: string;
  total_numbers: number;
  processed_numbers: number;
  valid_numbers: number;
  invalid_numbers: number;
  total_cost: string;
  created_at: string;
  completed_at: string | null;
}

const lookupRepo = new AdminListRepository<LookupRow>(apiClient, 'hlr-lookups');
const batchRepo = new AdminListRepository<BatchRow>(apiClient, 'hlr-batches');

function LookupsTab() {
  const [search, setSearch] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    return f;
  }, [search]);

  const cols: DataTableColumn<LookupRow>[] = [
    { id: 'created_at', header: 'When', accessor: (r) => formatDateTime(r.created_at) },
    {
      id: 'customer', header: 'Customer',
      accessor: (r) => (
        <span>
          {r.company_name || '—'}
          <span className="block text-xs text-slate-500">{r.customer_email}{r.batch ? ' · batch' : ''}</span>
        </span>
      ),
    },
    { id: 'phone_number', header: 'Phone', accessor: (r) => <code className="font-mono text-xs">{r.phone_number}</code> },
    {
      id: 'flags', header: 'Result',
      accessor: (r) => (
        <span className="space-x-1 text-xs">
          {r.error
            ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800" title={r.error}>failed</span>
            : r.state !== 'done'
              ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">{r.state}</span>
              : r.is_valid
                ? <span className="rounded bg-green-100 px-1.5 py-0.5 text-green-800">valid</span>
                : <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800">invalid</span>}
          {r.is_ported && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">ported</span>}
        </span>
      ),
    },
    { id: 'number_type', header: 'Type', accessor: 'number_type' },
    {
      id: 'carrier_name', header: 'Carrier',
      accessor: (r) => (
        <span>
          {r.carrier_name || '—'}
          {(r.country_code || r.mccmnc) && (
            <span className="block text-xs text-slate-500">{[r.country_code, r.mccmnc].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      ),
    },
    {
      id: 'price', header: 'Charge',
      accessor: (r) => (
        <span className={r.refunded ? 'text-slate-400 line-through' : ''}>
          ${r.price}{r.refunded && <span className="ml-1 text-xs no-underline text-slate-500">refunded</span>}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <input
          type="search"
          placeholder="Search phone, company or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>
      <DataTable<LookupRow>
        columns={cols}
        fetcher={(p) => lookupRepo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No number lookups yet.</span>}
      />
    </div>
  );
}

function BatchesTab() {
  const cols: DataTableColumn<BatchRow>[] = [
    { id: 'created_at', header: 'Started', accessor: (r) => formatDateTime(r.created_at) },
    { id: 'filename', header: 'File', accessor: 'filename' },
    { id: 'user_email', header: 'User', accessor: 'user_email' },
    { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
    { id: 'progress', header: 'Progress', accessor: (r) => `${r.processed_numbers}/${r.total_numbers}` },
    { id: 'valid', header: 'Valid', accessor: 'valid_numbers' },
    { id: 'invalid', header: 'Invalid', accessor: 'invalid_numbers' },
    { id: 'cost', header: 'Cost', accessor: (r) => `$${r.total_cost}` },
  ];

  return (
    <DataTable<BatchRow>
      columns={cols}
      fetcher={(p) => batchRepo.list(p)}
      pagination="offset"
      defaultPageSize={25}
      rowKey={(r) => String(r.id)}
      emptyState={<span data-testid="datatable-empty">No batch jobs yet.</span>}
    />
  );
}

export function HLRPage() {
  return (
    <TabbedListSection
      title="HLR (Number Validation)"
      tabs={[
        { id: 'lookups', label: 'Lookups', render: () => <LookupsTab /> },
        { id: 'batches', label: 'Batches', render: () => <BatchesTab /> },
      ]}
    />
  );
}
